import React from 'react'
import styled from 'styled-components'
import { financeColors, financeRadius, financeSpacing } from '../tokens'

interface Props {
  title?: React.ReactNode
  message: React.ReactNode
  actions?: React.ReactNode
  tone?: 'info' | 'warning' | 'error' | 'primary'
  dismiss?: React.ReactNode
}

const toneMap = {
  info: {
    background: financeColors.infoBg,
    border: financeColors.infoBorder,
    title: '#1d4ed8',
    message: '#1e3a8a',
  },
  warning: {
    background: financeColors.warningBg,
    border: financeColors.warningBorder,
    title: '#92400e',
    message: '#7c5b12',
  },
  error: {
    background: financeColors.dangerBg,
    border: financeColors.dangerBorder,
    title: '#b91c1c',
    message: '#7f1d1d',
  },
  primary: {
    background: '#ecfeff',
    border: '#99f6e4',
    title: '#0f766e',
    message: '#115e59',
  },
} as const

const Wrap = styled.div`
  margin: 0 20px 16px;
`

const Shell = styled.div<{ $tone: keyof typeof toneMap }>`
  width: 100%;
  border-radius: ${financeRadius.md};
  border: 1px solid ${({ $tone }) => toneMap[$tone].border};
  background: ${({ $tone }) => toneMap[$tone].background};
  padding: ${financeSpacing.md};
`

const Content = styled.div`
  display: grid;
  gap: 10px;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
`

const Title = styled.div<{ $tone: keyof typeof toneMap }>`
  font-size: 15px;
  font-weight: 700;
  color: ${({ $tone }) => toneMap[$tone].title};
`

const Message = styled.div<{ $tone: keyof typeof toneMap }>`
  color: ${({ $tone }) => toneMap[$tone].message};
  font-size: 13px;
  line-height: 1.5;
`

const FinancePageBanner: React.FC<Props> = ({
  title,
  message,
  actions,
  tone = 'warning',
  dismiss,
}) => (
  <Wrap>
    <Shell $tone={tone}>
      <Content>
        <Header>
          <div>{title && <Title $tone={tone}>{title}</Title>}</div>
          {dismiss}
        </Header>
        <Message $tone={tone}>{message}</Message>
        {actions}
      </Content>
    </Shell>
  </Wrap>
)

export default FinancePageBanner
